/**
 * OaSIS Explorer Frontend - URL State Management Hook
 * Task 9.1: Advanced Search URL State Management
 * 
 * Hook for managing Advanced Search state in URL query parameters
 * Enables shareable and bookmarkable search URLs
 */

import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useRef } from 'react';
import {
  encodeFilters,
  decodeFilters,
  encodeSearchQuery,
  decodeSearchQuery,
  encodeHierarchyPrefixes,
  decodeHierarchyPrefixes,
  encodeTeerLevels,
  decodeTeerLevels,
} from '../utils/urlStateUtils';
import { getFieldFullName, getFieldShorthand } from '../utils/fieldShorthandMapping';

/**
 * Advanced Search URL state structure
 */
export interface AdvancedSearchUrlState {
  textSearch: string;
  hierarchyPrefixes: string[];
  teerLevels: number[]; // Array of selected TEER levels (0-5)
  measurements: Record<string, { min?: number; max?: number; exact?: number | string }>;
}

/**
 * Hook for managing Advanced Search state in URL query parameters
 * 
 * Provides bidirectional sync between URL and component state:
 * - On mount: Read state from URL
 * - On state change: Update URL (debounced)
 * 
 * @example
 * ```typescript
 * const { getStateFromUrl, updateUrl } = useAdvancedSearchUrlState();
 * 
 * // Initialize from URL on mount
 * useEffect(() => {
 *   const urlState = getStateFromUrl();
 *   setFilters(urlState);
 * }, []);
 * 
 * // Update URL when state changes
 * useEffect(() => {
 *   updateUrl(filters);
 * }, [filters]);
 * ```
 */
export const useAdvancedSearchUrlState = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const updateTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  /**
   * Read current state from URL query parameters
   * 
   * @returns Current state decoded from URL
   */
  const getStateFromUrl = useCallback((): AdvancedSearchUrlState => {
    const queryParam = searchParams.get('q');
    const reservedParams = new Set(['q', 'pref', 'sort', 'page', 'limit', 'view']);
    const isLikelyMeasurementValue = (rawValue: string): boolean => {
      const value = rawValue.trim();
      return (
        /^>=-?\d+(?:\.\d+)?$/.test(value) ||
        /^<=-?\d+(?:\.\d+)?$/.test(value) ||
        /^-?\d+(?:\.\d+)?$/.test(value) ||
        /^-?\d+(?:\.\d+)?--?\d+(?:\.\d+)?$/.test(value) ||
        /^[A-Za-z]$/.test(value) ||
        /^[A-Za-z](?:,[A-Za-z])+$/i.test(value)
      );
    };

    // Collect all measurement filter parameters
    const measurementParams: string[] = [];
    searchParams.forEach((value, key) => {
      // Skip non-measurement parameters
      // Skip: q (text search), pref (page referrer), sort/page/limit (pagination/sorting)
      // Skip hierarchy (h0, h1, etc.) and TEER (t0, t1, etc.) params, not all 'h'/'t' prefixes
      if (reservedParams.has(key) ||
          (key.startsWith('h') && key.length === 2 && /^h\d$/.test(key)) ||
          (key.startsWith('t') && key.length === 2 && /^t\d$/.test(key))) {
        return;
      }
      
      // Accept:
      // - shorthand keys (e.g. accn)
      // - full measurement keys with known shorthand (e.g. accounting)
      // - full keys with filter-shaped values (e.g. generalLearningAbility=3-3)
      const fieldName = getFieldFullName(key) || key;
      const isShorthand = fieldName !== key;
      const hasShorthand = getFieldShorthand(fieldName) !== null;
      const looksLikeMeasurement = isLikelyMeasurementValue(value);
      if (isShorthand || hasShorthand || looksLikeMeasurement) {
        measurementParams.push(`${key}=${value}`);
      }
    });

    // Use compact format
    const hierarchyPrefixes = decodeHierarchyPrefixes(searchParams);
    const teerLevels = decodeTeerLevels(searchParams);

    return {
      textSearch: decodeSearchQuery(queryParam),
      hierarchyPrefixes,
      teerLevels,
      measurements: decodeFilters(measurementParams.join('&')),
    };
  }, [searchParams]);

  /**
   * Update URL with current state
   * 
   * Uses debouncing to avoid excessive history entries during typing.
   * Can be called immediately for significant changes (e.g., hierarchy selection).
   * 
   * @param state - Current application state
   * @param immediate - If true, update immediately without debouncing
   */
  const updateUrl = useCallback(
    (state: AdvancedSearchUrlState, immediate = false) => {
      const updateFn = () => {
        // Preserve existing non-filter parameters (like 'sort', 'pref')
        const currentParams = new URLSearchParams(searchParams);
        const preservedParams = ['sort', 'page', 'limit', 'pref'];
        const preserved: Record<string, string> = {};
        preservedParams.forEach(key => {
          const value = currentParams.get(key);
          if (value) preserved[key] = value;
        });
        
        const params = new URLSearchParams();

        // Add text search query if not empty
        if (state.textSearch && state.textSearch.trim()) {
          params.set('q', encodeSearchQuery(state.textSearch));
        }

        // Add hierarchy prefixes as individual parameters if any
        if (state.hierarchyPrefixes.length > 0) {
          const hierarchyParams = encodeHierarchyPrefixes(state.hierarchyPrefixes);
          Object.entries(hierarchyParams).forEach(([key, value]) => {
            params.set(key, value);
          });
        }

        // Add TEER levels as individual parameters if any
        if (state.teerLevels.length > 0) {
          const teerParams = encodeTeerLevels(state.teerLevels);
          Object.entries(teerParams).forEach(([key, value]) => {
            params.set(key, value);
          });
        }

        // Add measurement filters as individual parameters
        const encodedFilters = encodeFilters(state.measurements);
        if (encodedFilters) {
          // Parse the compact format and add each filter as individual param
          const filterParts = encodedFilters.split('&');
          filterParts.forEach(filterPart => {
            if (filterPart.includes('=')) {
              const [key, value] = filterPart.split('=');
              params.set(key, value);
            } else if (filterPart.includes('>=')) {
              const [key, value] = filterPart.split('>=');
              params.set(key, `>=${value}`);
            } else if (filterPart.includes('<=')) {
              const [key, value] = filterPart.split('<=');
              params.set(key, `<=${value}`);
            }
          });
        }

        // Restore preserved params
        Object.entries(preserved).forEach(([key, value]) => {
          params.set(key, value);
        });

        // Replace URL (don't push to history)
        // This avoids polluting browser history on every filter change
        setSearchParams(params, { replace: true });
      };

      if (immediate) {
        // Clear any pending debounced update
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }
        updateFn();
      } else {
        // Debounce by 500ms to avoid excessive updates during typing
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }
        updateTimeoutRef.current = setTimeout(updateFn, 500);
      }
    },
    [setSearchParams, searchParams]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  return {
    getStateFromUrl,
    updateUrl,
  };
};




